/** Natural country colours for the local ground and its cached terrain marks. */
import { mix } from '../data/palette.ts';
import { Terrain } from '../sim/types.ts';

/** RGB output range only. Snow is allowed to be white; this is not an art cap. */
export const TERRAIN_MAX_LUMA = 255;
/** Sparse snow highlights may be pale without bleaching the entire texture. */
export const MARK_MAX_LUMA = 245;

export function luma(c: number): number {
  return .299 * ((c >> 16) & 255) + .587 * ((c >> 8) & 255) + .114 * (c & 255);
}
/** Optional explicit limit for marks; scaling preserves the source hue. */
export function capLuma(c: number, max = TERRAIN_MAX_LUMA): number {
  const value = luma(c);
  if (value <= max) return c;
  const scale = max / value;
  return (Math.round(((c >> 16) & 255) * scale) << 16) |
    (Math.round(((c >> 8) & 255) * scale) << 8) | Math.round((c & 255) * scale);
}

export type FabricKind = 'grit' | 'turf' | 'litter' | 'scree' | 'dune' | 'drift' | 'reed' | 'shingle' | 'rubble' | 'current' | 'plank' | 'rut';
export type GroundGroup = 'dry' | 'wood' | 'rock' | 'wet' | 'water' | 'built';
const GROUP_OF: Record<number, GroundGroup> = {
  [Terrain.AshPlain]: 'dry', [Terrain.Ruin]: 'built', [Terrain.Forest]: 'wood',
  [Terrain.StoneOutcrop]: 'rock', [Terrain.IronSeam]: 'rock', [Terrain.Marsh]: 'wet',
  [Terrain.River]: 'water', [Terrain.Road]: 'built', [Terrain.Ditch]: 'built',
  [Terrain.Leyline]: 'dry', [Terrain.Deep]: 'water', [Terrain.Bridge]: 'built',
};
export function groupOf(terrain: number): GroundGroup { return GROUP_OF[terrain] ?? 'dry'; }

export interface Surface {
  base: number;
  alt: number;
  ink: number;
  /** Render-only height, in tiles. */
  rise: number;
  rough: number;
  /** Keeps roads and shorelines crisp through the ground's colour blur. */
  hard: number;
  fabric: FabricKind;
  fabricGain: number;
}

/** Grass, weathered stone, blue water and warm tracks remain distinct surfaces. */
const BASE: Record<number, Surface> = {
  [Terrain.AshPlain]: { base: 0x598b43, alt: 0x8ca862, ink: 0x386138, rise: 0, rough: 1, hard: .12, fabric: 'turf', fabricGain: 1 },
  [Terrain.Ruin]: { base: 0x828675, alt: 0xa2a693, ink: 0x565e4d, rise: .1, rough: .7, hard: .4, fabric: 'rubble', fabricGain: .7 },
  [Terrain.Forest]: { base: 0x47753f, alt: 0x789753, ink: 0x2c5335, rise: .09, rough: .85, hard: .2, fabric: 'litter', fabricGain: .8 },
  [Terrain.StoneOutcrop]: { base: 0x7a8776, alt: 0xa3ad98, ink: 0x54675c, rise: .5, rough: 1.4, hard: .3, fabric: 'scree', fabricGain: .7 },
  [Terrain.IronSeam]: { base: 0x667566, alt: 0x8a8e75, ink: 0x465d51, rise: .3, rough: 1.15, hard: .3, fabric: 'scree', fabricGain: .65 },
  [Terrain.Marsh]: { base: 0x4e7960, alt: 0x809a72, ink: 0x34554b, rise: -.16, rough: .25, hard: .18, fabric: 'reed', fabricGain: .8 },
  // Water depth is colour, rather than an exaggerated bank-height halo.
  [Terrain.River]: { base: 0x477f8b, alt: 0x91b0b0, ink: 0x305968, rise: -.18, rough: .2, hard: .74, fabric: 'current', fabricGain: .6 },
  [Terrain.Deep]: { base: 0x29576e, alt: 0x5d8897, ink: 0x203f56, rise: -.34, rough: .1, hard: .74, fabric: 'current', fabricGain: .4 },
  [Terrain.Road]: { base: 0xa18d68, alt: 0xbfa77a, ink: 0x726248, rise: -.05, rough: .3, hard: .7, fabric: 'rut', fabricGain: .65 },
  [Terrain.Ditch]: { base: 0x3c493d, alt: 0x65735a, ink: 0x28362e, rise: -.5, rough: .4, hard: .72, fabric: 'grit', fabricGain: .6 },
  [Terrain.Leyline]: { base: 0x6c8097, alt: 0x94b1cc, ink: 0x465971, rise: 0, rough: .8, hard: .5, fabric: 'grit', fabricGain: .5 },
  [Terrain.Bridge]: { base: 0xa18b63, alt: 0xc4ad7a, ink: 0x67563d, rise: .14, rough: 0, hard: .95, fabric: 'plank', fabricGain: .65 },
};
interface Pull { c: number; t: number }
export interface BiomeTheme {
  id: string;
  pull: Partial<Record<GroundGroup, Pull>>;
  sun: number;
  shadow: number;
  key: number;
  relief: number;
  open: FabricKind;
  grain: number;
  /** Multiply tint for existing trees, rocks and debris. */
  prop: number;
}

/** Material identity comes from the country, while relief remains restrained. */
export const BIOME_THEMES: Record<string, BiomeTheme> = {
  plain: {
    id: 'plain', pull: {}, sun: 0xe6ebc9, shadow: 0x354e3d,
    key: .5, relief: .3, open: 'turf', grain: .85, prop: 0xffffff,
  },
  forest: {
    id: 'forest', pull: {
      dry: { c: 0x4e8b42, t: .55 }, wood: { c: 0x386a38, t: .45 },
      rock: { c: 0x748570, t: .3 }, wet: { c: 0x46765a, t: .35 },
      water: { c: 0x3d7b79, t: .22 }, built: { c: 0x938767, t: .15 },
    },
    sun: 0xd7e7b9, shadow: 0x2d503b, key: .42, relief: .22,
    open: 'litter', grain: .85, prop: 0xe4eed4,
  },
  jungle: {
    id: 'jungle', pull: {
      dry: { c: 0x378549, t: .75 }, wood: { c: 0x2c6b3d, t: .6 },
      rock: { c: 0x6f8974, t: .35 }, wet: { c: 0x3b7460, t: .45 },
      water: { c: 0x347575, t: .3 }, built: { c: 0x948667, t: .18 },
    },
    sun: 0xd0e7b0, shadow: 0x254e3c, key: .36, relief: .25,
    open: 'litter', grain: .92, prop: 0xd9edc8,
  },
  hill: {
    id: 'hill', pull: {
      dry: { c: 0x719443, t: .42 }, rock: { c: 0x919b80, t: .35 },
      wet: { c: 0x647b54, t: .25 }, built: { c: 0xa18f69, t: .16 },
    },
    sun: 0xe9ebc2, shadow: 0x3e5340, key: .62, relief: .52,
    open: 'turf', grain: .8, prop: 0xf2f0df,
  },
  mountain: {
    id: 'mountain', pull: {
      dry: { c: 0x929c88, t: .68 }, wood: { c: 0x607c52, t: .4 },
      rock: { c: 0x98a29b, t: .55 }, wet: { c: 0x71877c, t: .4 },
      water: { c: 0x527f98, t: .28 }, built: { c: 0x9a9682, t: .3 },
    },
    sun: 0xeeeedd, shadow: 0x4a5c61, key: .64, relief: .72,
    open: 'scree', grain: .85, prop: 0xeef0ef,
  },
  ice: {
    id: 'ice', pull: {
      dry: { c: 0xf4f7fa, t: 1 }, wood: { c: 0xeaf0f2, t: .98 },
      rock: { c: 0xcddae0, t: .88 }, wet: { c: 0xdce9ef, t: 1 },
      water: { c: 0x729bb5, t: .42 }, built: { c: 0xc6cdd0, t: .25 },
    },
    sun: 0xffffff, shadow: 0xabbfd4, key: .58, relief: .4,
    open: 'drift', grain: .7, prop: 0xe7eff8,
  },
  desert: {
    id: 'desert', pull: {
      dry: { c: 0xcbb27b, t: .97 }, wood: { c: 0x969052, t: .55 },
      rock: { c: 0xac9f83, t: .66 }, wet: { c: 0x899568, t: .55 },
      water: { c: 0x458d92, t: .25 }, built: { c: 0xad926b, t: .4 },
    },
    sun: 0xfae8bc, shadow: 0x8a776a, key: .62, relief: .46,
    open: 'dune', grain: .8, prop: 0xf5e6c9,
  },
  marsh: {
    id: 'marsh', pull: {
      dry: { c: 0x729976, t: .66 }, wood: { c: 0x527c53, t: .35 },
      rock: { c: 0x7e9082, t: .3 }, wet: { c: 0x548574, t: .7 },
      water: { c: 0x3f6f79, t: .35 }, built: { c: 0x969174, t: .22 },
    },
    sun: 0xd5e1c9, shadow: 0x34594e, key: .3, relief: .08,
    open: 'reed', grain: .8, prop: 0xd5e0ce,
  },
  coast: {
    id: 'coast', pull: {
      dry: { c: 0xc3bd91, t: .84 }, wood: { c: 0x658c56, t: .4 },
      rock: { c: 0x98a595, t: .4 }, wet: { c: 0x759c91, t: .42 },
      water: { c: 0x397d98, t: .45 }, built: { c: 0xa79e80, t: .25 },
    },
    sun: 0xeee8cf, shadow: 0x506b6b, key: .46, relief: .14,
    open: 'shingle', grain: .65, prop: 0xf0eddf,
  },
  ruin: {
    id: 'ruin', pull: {
      dry: { c: 0x879074, t: .65 }, wood: { c: 0x567948, t: .3 },
      rock: { c: 0x959889, t: .4 }, wet: { c: 0x6d826c, t: .3 },
      built: { c: 0x95907c, t: .35 },
    },
    sun: 0xe6dec2, shadow: 0x4c5845, key: .52, relief: .2,
    open: 'rubble', grain: .8, prop: 0xede4cf,
  },
};

export function themeFor(biome: string | undefined): BiomeTheme {
  return BIOME_THEMES[biome ?? 'plain'] ?? BIOME_THEMES.plain;
}
export function surfaceFor(theme: BiomeTheme, terrain: number): Surface {
  const b = BASE[terrain] ?? BASE[Terrain.AshPlain], group = groupOf(terrain);
  const p = theme.pull[group];
  const snow = theme.id === 'ice' && (terrain === Terrain.AshPlain || group === 'wood' || group === 'wet');
  const fabric = snow ? 'drift' : terrain === Terrain.AshPlain ? theme.open : b.fabric;
  if (!p) return fabric === b.fabric ? b : { ...b, fabric };
  // Tracks remain usable-looking ground, ditches remain holes, and magical
  // seams keep their blue identity even in a snowy or sandy country.
  const amount = p.t * (terrain === Terrain.Ditch ? .16 : terrain === Terrain.Leyline ? .3 : 1);
  return { ...b, base: mix(b.base, p.c, amount), alt: mix(b.alt, p.c, amount),
    ink: mix(b.ink, p.c, amount * .7), fabric };
}
export function surfaceTable(theme: BiomeTheme): Surface[] {
  const out: Surface[] = [];
  for (const key of Object.keys(BASE)) out[Number(key)] = surfaceFor(theme, Number(key));
  return out;
}
export const TERRAIN_IDS: readonly number[] = Object.keys(BASE).map(Number);
